import { useCallback, useEffect, useRef, useState } from 'react';

import type { SheetAppointment, SheetReport } from '../lib/crm/accountSheet';
import { onboardingOf, type LeadOnboarding, type LeadRow } from '../lib/crm/leadSheet';
import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import type { CRMNote } from '../types/notes';

type Named = { id: string; name: string };

/** The lead as its sheet reads it: the row, its account, its deal. */
export interface SheetLead {
  row: LeadRow;
  /** Its account by its key (`account_id`), else its deal's, else its company's: who the lead is, in the CRM. */
  account: Named | null;
  opportunity: Named | null;
}

export interface ChecklistTally {
  done: number;
  total: number;
}

export interface LeadRows {
  appointments: SheetAppointment[];
  notes: CRMNote[];
  reports: SheetReport[];
  /** Otto's pre-meeting briefs on its appointments (agent notes tagged `prebrief`). */
  briefs: CRMNote[];
  checklists: { client: ChecklistTally | null; internal: ChecklistTally | null };
}

export interface LeadSheetData {
  lead: SheetLead | null;
  /** Its onboarding as the back reads it; null when unreadable (the rest still shows). */
  onboarding: LeadOnboarding | null;
  missing: boolean;
  error: string | null;
  rows: LeadRows;
  rowsReady: boolean;
  rowsError: string | null;
  /** Read the lead and its onboarding again (after an edit, a stage moved). */
  reload: () => Promise<void>;
}

const NO_ROWS: LeadRows = { appointments: [], notes: [], reports: [], briefs: [], checklists: { client: null, internal: null } };

async function readLead(id: string): Promise<{ lead: SheetLead | null; error: string | null }> {
  const { data, error } = await supabase.from('onboarding_leads').select('*').eq('id', id).maybeSingle();
  if (error) return { lead: null, error: error.message };
  if (!data) return { lead: null, error: null };
  const [opportunity, accountFromKey] = await Promise.all([
    data.crm_opportunity_id
      ? supabase.from('crm_opportunities').select('id, name, account_id').eq('id', data.crm_opportunity_id).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    data.account_id ? supabase.from('crm_accounts').select('id, name').eq('id', data.account_id).maybeSingle() : Promise.resolve({ data: null, error: null }),
  ]);
  let account: Named | null = accountFromKey.data ? { id: accountFromKey.data.id, name: accountFromKey.data.name } : null;
  // No account by key: its deal's (the way the lead was made from an opportunity)…
  if (!account && opportunity.data?.account_id) {
    const { data: viaDeal } = await supabase.from('crm_accounts').select('id, name').eq('id', opportunity.data.account_id).maybeSingle();
    if (viaDeal) account = { id: viaDeal.id, name: viaDeal.name };
  }
  // …else its company's oldest account, as the old panel read it: a lead made by « Nouvelle
  // entreprise » gets an account from `sync_lead_to_account` without `account_id` written back.
  if (!account && data.company_id) {
    const { data: viaCompany } = await supabase
      .from('crm_accounts')
      .select('id, name')
      .eq('company_id', data.company_id)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (viaCompany) account = { id: viaCompany.id, name: viaCompany.name };
  }
  return {
    lead: {
      row: data,
      account,
      opportunity: opportunity.data ? { id: opportunity.data.id, name: opportunity.data.name } : null,
    },
    error: null,
  };
}

async function readOnboarding(id: string): Promise<LeadOnboarding | null> {
  const { data, error } = await supabase.rpc('get_onboarding_pipeline', { p_lead_id: id });
  if (error) {
    logWarn('crm', 'lead sheet: onboarding unreadable', { leadId: id, error: error.message });
    return null;
  }
  return onboardingOf(data);
}

const tally = (items: { is_completed: boolean | null }[] | null | undefined): ChecklistTally | null =>
  items && items.length > 0 ? { done: items.filter((i) => i.is_completed).length, total: items.length } : null;

async function readRows(id: string): Promise<LeadRows> {
  const [appointments, notes, checklists] = await Promise.all([
    supabase
      .from('appointments')
      .select('id, start_time, title, status, appointment_type:appointment_types(name), contact:crm_contacts(first_name, last_name)')
      .eq('onboarding_lead_id', id)
      .order('start_time', { ascending: false })
      .limit(10),
    supabase.from('crm_notes').select('*').eq('lead_id', id).order('created_at', { ascending: false }).limit(10),
    supabase.from('onboarding_checklists').select('checklist_type, items:onboarding_checklist_items(is_completed)').eq('lead_id', id),
  ]);
  const failed = appointments.error ?? notes.error ?? checklists.error;
  if (failed) throw new Error(failed.message);
  const appointmentIds = (appointments.data ?? []).map((a) => a.id);
  const noteIds = (notes.data ?? []).map((n) => n.id);
  // Its meeting reports and Otto's briefs: optional, unreadable they leave the rest shown.
  const filters = [
    appointmentIds.length > 0 ? `appointment_id.in.(${appointmentIds.join(',')})` : null,
    noteIds.length > 0 ? `note_id.in.(${noteIds.join(',')})` : null,
  ].filter((f): f is string => f !== null);
  const [reports, briefs] = await Promise.all([
    filters.length > 0
      ? supabase.from('crm_meeting_reports').select('id, cr_title, created_at').or(filters.join(',')).order('created_at', { ascending: false }).limit(5)
      : Promise.resolve({ data: [], error: null }),
    appointmentIds.length > 0
      ? supabase.from('crm_notes').select('*').in('appointment_id', appointmentIds).eq('source', 'agent').order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (reports.error) logWarn('crm', 'lead sheet: meeting reports unreadable', { leadId: id, error: reports.error.message });
  if (briefs.error) logWarn('crm', 'lead sheet: briefs unreadable', { leadId: id, error: briefs.error.message });
  const lists = checklists.data ?? [];
  return {
    appointments: (appointments.data ?? []).map((a) => ({
      id: a.id,
      start_time: a.start_time,
      status: a.status,
      contact: a.contact,
      // An appointment without a title says its type (« Démo »).
      title: a.title?.trim() || (a.appointment_type as { name?: string | null } | null)?.name || null,
    })),
    // Otto's briefs have their own block: never twice, as on the account's record.
    notes: (notes.data ?? [])
      .filter((n) => !(n.source === 'agent' && (n.tags ?? []).includes('prebrief')))
      .map((n) => ({ ...n, created_at: n.created_at ?? '' })) as unknown as CRMNote[],
    reports: (reports.data ?? []).map((r) => ({ id: r.id, cr_title: r.cr_title, created_at: r.created_at ?? '' })),
    briefs: ((briefs.data ?? []) as unknown as CRMNote[]).filter((n) => (n.tags ?? []).includes('prebrief')),
    checklists: {
      client: tally(lists.find((c) => c.checklist_type === 'client')?.items),
      internal: tally(lists.find((c) => c.checklist_type === 'internal')?.items),
    },
  };
}

/**
 * An onboarding lead's working sheet (PLAN-ENCRE-OUTILS O3 bis): the lead (its account by its key,
 * its deal), its onboarding as the back reads it — stage, since when, the exit criteria met or not —,
 * then its appointments, notes, meeting reports, Otto's briefs and checklists. Every answer belongs
 * to ONE lead; of two reads of the same lead the older one arriving last is dropped; a failed re-read
 * keeps the lead shown.
 */
export function useLeadSheet(leadId: string | null): LeadSheetData {
  const [lead, setLead] = useState<SheetLead | null>(null);
  const [onboarding, setOnboarding] = useState<LeadOnboarding | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<LeadRows>(NO_ROWS);
  const [rowsFor, setRowsFor] = useState<string | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const latest = useRef<string | null>(null);
  const readCount = useRef(0);

  const onboardingCount = useRef(0);

  // The lead shows as soon as it is read; its onboarding (a heavier read) follows on its own.
  const loadLead = useCallback(async (id: string) => {
    const ticket = ++readCount.current;
    const stageTicket = ++onboardingCount.current;
    const stage = readOnboarding(id).then((read) => {
      if (latest.current !== id || stageTicket !== onboardingCount.current) return;
      setOnboarding((shown) => read ?? shown);
    });
    const read = await readLead(id);
    if (latest.current === id && ticket === readCount.current) {
      if (read.error) logWarn('crm', 'lead sheet: lead unreadable', { leadId: id, error: read.error });
      setLead((shown) => (read.error && shown?.row.id === id ? shown : read.lead));
      setError(read.error);
      setMissing(!read.error && !read.lead);
    }
    await stage;
  }, []);

  useEffect(() => {
    latest.current = leadId;
    setLead(null);
    setOnboarding(null);
    setMissing(false);
    setError(null);
    setRows(NO_ROWS);
    setRowsFor(null);
    setRowsError(null);
    if (!leadId) return;
    const id = leadId;
    void loadLead(id);
    readRows(id)
      .then((read) => {
        if (latest.current !== id) return;
        setRows(read);
        setRowsFor(id);
      })
      .catch((err: unknown) => {
        if (latest.current !== id) return;
        const message = err instanceof Error ? err.message : String(err);
        logWarn('crm', 'lead sheet: rows unreadable', { leadId: id, error: message });
        setRowsError(message);
      });
  }, [leadId, loadLead]);

  const reload = useCallback(async () => {
    if (leadId) await loadLead(leadId);
  }, [leadId, loadLead]);

  return { lead, onboarding, missing, error, rows, rowsReady: leadId !== null && rowsFor === leadId, rowsError, reload };
}
